/**
 * Utilitaires pour réinitialiser les watchdogs via KV
 * Frontend demande au worker wab-config d'écrire une clé de reset dans KV
 * Le worker détecte la clé et applique le reset automatiquement
 */

import debug from '@commons-shared/debug.js';
import { config } from '../config/environment.js';
import { routedFetch } from './api-router.js';

/**
 * Demander une réinitialisation du watchdog via KV
 * Contacte l'endpoint /api/config/tool/cb-kv/request-reset
 * @param {string} workerName - Nom du worker (ex: 'wab-iaproxy', 'wab-waproxy')
 * @returns {Promise<Object>} Réponse de l'API
 */
export async function requestWatchdogResetViaKV(workerName) {
  try {
    debug.log(config, `Demande de reset watchdog pour ${workerName} via KV`);
    
    const response = await routedFetch('/api/config/tool/cb-kv/request-reset', {
      method: 'POST',
      body: { workerName }
    });
    
    if (response && response.success) {
      debug.log(config, `Reset watchdog demandé avec succès pour ${workerName}: ${response.message}`);
      return response;
    } else {
      const errorMsg = response?.error || 'Erreur inconnue lors de la demande de reset';
      debug.error(config, `Erreur lors du reset watchdog pour ${workerName}: ${errorMsg}`);
      throw new Error(errorMsg);
    }
  } catch (error) {
    debug.error(config, `Échec de la demande de reset watchdog pour ${workerName}:`, error);
    throw error;
  }
}

/**
 * Mapper une route de circuit breaker au nom du worker
 * @param {string} circuitRoute - Route du circuit breaker (ex: '/api/iaproxy/text')
 * @returns {string} Nom du worker (ex: 'wab-iaproxy')
 */
export function mapRouteToWorkerName(circuitRoute) {
  if (circuitRoute.startsWith('/api/ia')) {
    return 'wab-iaproxy';
  } else if (circuitRoute.startsWith('/api/wa')) {
    return 'wab-waproxy';
  } else if (circuitRoute.startsWith('/api/config')) {
    return 'wab-config';
  } else if (circuitRoute.startsWith('/api/chat')) {
    return 'wab-chatbot';
  }
  
  // Fallback : utiliser le dernier segment de la route
  const segments = circuitRoute.split('/');
  return segments[segments.length - 1] || 'unknown';
}

export default {
  requestWatchdogResetViaKV,
  mapRouteToWorkerName
};
